import React, { useState, useEffect } from 'react';
import {
  Layout,
  Typography,
  Button,
  Card,
  Input,
  Flex,
  theme,
  message,
  Upload as AntUpload,
  Spin,
  Modal,
  Space,
  Divider,
  Collapse,
  Tag,
} from 'antd';
import {
  LeftOutlined,
  FileTextOutlined,
  GlobalOutlined,
  UploadOutlined,
  RobotOutlined,
  PlayCircleOutlined,
  SaveOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import Editor from '@monaco-editor/react';
import { ConsoleViewer } from './ConsoleViewer';
import { myAssistantServices } from '../../../../services/myAssistantServices';
import { Chatbot } from '../../../../../../Components/Chatbot/Chatbot';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { Content } = Layout;
const { Title, Text } = Typography;
const { useToken } = theme;
const { Dragger } = AntUpload;
const { Panel } = Collapse;

interface CustomIntegrationProps {
  onBack: () => void;
  onSave: (data: any) => void;
  chatbotId?: string;
}

interface KnowledgeSource {
  title: string;
  description: string;
  icon: React.ReactNode;
  action: string;
  onClick: () => void;
  iconBackground: string;
}

export const CustomIntegration: React.FC<CustomIntegrationProps> = ({
  onBack,
  onSave,
  chatbotId,
}) => {
  const { token } = useToken();
  
  // State for files
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileModalVisible, setFileModalVisible] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [uploadedDocument, setUploadedDocument] = useState<{name: string, content: string} | null>(null);
  
  // State for websites
  const [websiteModalVisible, setWebsiteModalVisible] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isWebsiteUploading, setIsWebsiteUploading] = useState(false);
  const [syncedWebsite, setSyncedWebsite] = useState<string | null>(null);
  
  // State for AI generation
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [showCodeEditor, setShowCodeEditor] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  
  // Console states
  const [consoleVisible, setConsoleVisible] = useState(false);
  const [httpCallLogs, setHttpCallLogs] = useState<any[]>([]);
  const [executionLogs, setExecutionLogs] = useState<any[]>([]);
  const [totalExecutionTime, setTotalExecutionTime] = useState(0);

  // Documentation modal state
  const [docsModalVisible, setDocsModalVisible] = useState(false);

  // Test parameters state
  const [testParametersModalVisible, setTestParametersModalVisible] = useState(false);
  const [testParameters, setTestParameters] = useState<string>('{}');
  const [testContext, setTestContext] = useState<string>('{}');

  // Chat generator states
  const [integrationGeneratorConfig, setIntegrationGeneratorConfig] = useState<any>(null);
  const [integrationChatbotId, setIntegrationChatbotId] = useState<string | null>(null);
  const channelId = React.useMemo(() => uuidv4(), []);

  // Default cloud WebSocket URL
  const DEFAULT_WEBSOCKET_URL = process.env.REACT_APP_WS_SHARED_URL;

  // Initialize chatbot: get ID by flowType, then get config
  useEffect(() => {
    const initializeChatbot = async () => {
      try {
        // Get chatbot ID dynamically by flowType
        const dynamicChatbotId = await myAssistantServices.getChatbotIdByFlowType('custom-integration');

        if (!dynamicChatbotId) {
          console.error('[Custom Integration Generator] No chatbot found for flowType "custom-integration"');
          return;
        }

        // Clear conversation history on reload
        localStorage.removeItem(`chatbot_messages_${dynamicChatbotId}`);
        localStorage.removeItem(`chatbot_greeting_shown_${dynamicChatbotId}`);

        setIntegrationChatbotId(dynamicChatbotId);

        // Get chatbot configuration
        const responseData = await myAssistantServices.getChatbotConfig(dynamicChatbotId);
        const webchatConfig = responseData.webchatConfig;
        const wsUrlToUse = DEFAULT_WEBSOCKET_URL || webchatConfig.wsUrl || process.env.REACT_APP_WS_URL;

        setIntegrationGeneratorConfig({
          botName: responseData.name || webchatConfig.botName || 'Custom Integration Generator',
          colorMain: webchatConfig.colorMain,
          colorTitle: webchatConfig.colorTitle,
          iconBgColor: webchatConfig.iconBgColor || '#ffffff',
          wsUrl: wsUrlToUse,
        });
      } catch (error) {
        console.error('[Custom Integration Generator] Error initializing chatbot:', error);
      }
    };

    initializeChatbot();
  }, []);

  // File upload configuration
  const fileUploadProps = {
    beforeUpload: (file: File) => {
      const isValid = 
        file.type === 'text/plain' || 
        file.type === 'application/pdf' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/msword';
      
      if (!isValid) {
        message.error('You can only upload TXT, PDF, DOC, or DOCX files!');
        return AntUpload.LIST_IGNORE;
      }
      
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('File must be smaller than 5MB!');
        return AntUpload.LIST_IGNORE;
      }
      
      setSelectedFile(file);
      return false;
    },
    onRemove: () => {
      setSelectedFile(null);
    },
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!selectedFile) {
      message.error('Please select a file to upload');
      return;
    }
    
    setIsFileUploading(true);
    try {
      // Read file content as base64
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setUploadedDocument({
          name: selectedFile.name,
          content: content // This includes the data:type/format;base64, prefix
        });
        message.success(`File "${selectedFile.name}" uploaded successfully`);
        setSelectedFile(null);
        setFileModalVisible(false);
        setIsFileUploading(false);
      };
      
      reader.onerror = () => {
        message.error('Failed to read file');
        setIsFileUploading(false);
      };
      
      reader.readAsDataURL(selectedFile);
    } catch (error) {
      message.error('Failed to upload file');
      setIsFileUploading(false);
    }
  };

  // Handle website sync
  const handleWebsiteSync = async () => {
    if (!websiteUrl) {
      message.error('Please enter a website URL');
      return;
    }
    
    // Basic URL validation
    try {
      new URL(websiteUrl);
    } catch {
      message.error('Please enter a valid URL');
      return;
    }
    
    setIsWebsiteUploading(true);
    try {
      // Simulate website syncing - you can implement actual website processing here
      await new Promise(resolve => setTimeout(resolve, 2000));
      message.success(`Website "${websiteUrl}" synced successfully`);
      setSyncedWebsite(websiteUrl);
      setWebsiteUrl('');
      setWebsiteModalVisible(false);
    } catch (error) {
      message.error('Failed to sync website');
    } finally {
      setIsWebsiteUploading(false);
    }
  };

  // Handler for automatic artifact acceptance from chatbot
  const handleArtifactAccept = async (artifact: { content: string, language?: string }) => {
    try {
      // The artifact content should contain the generated integration code
      // It might be wrapped in JSON or be plain code
      let generatedCode = '';
      
      try {
        // Try to parse as JSON first (in case it's wrapped)
        const parsed = JSON.parse(artifact.content);
        // If it's an object with a code field, extract it
        if (parsed.code || parsed.generatedCode) {
          generatedCode = parsed.code || parsed.generatedCode;
        } else if (typeof parsed === 'string') {
          generatedCode = parsed;
        } else {
          // If it's an object, stringify it (might be a code structure)
          generatedCode = JSON.stringify(parsed, null, 2);
        }
      } catch {
        // If not JSON, use the content directly as code
        generatedCode = artifact.content;
      }

      if (!generatedCode || generatedCode.trim().length === 0) {
        message.warning('Generated code is empty. Please try again.');
        return;
      }

      // Convert literal \n strings to actual newlines
      // The LLM generates code with \n as text, we need to convert them to real newlines
      generatedCode = generatedCode.replace(/\\n/g, '\n');
      
      // Also handle other common escape sequences that might be literal
      generatedCode = generatedCode.replace(/\\t/g, '\t');
      generatedCode = generatedCode.replace(/\\r/g, '\r');

      setGeneratedCode(generatedCode);
      setShowCodeEditor(true);
      
      message.success('Custom integration code generated successfully from chat');
    } catch (error) {
      console.error('Error processing artifact:', error);
      message.error('Invalid artifact format. Expected integration code.');
    }
  };

  // Check if code uses parameters
  const codeUsesParameters = (code: string): boolean => {
    return /parameters\./.test(code) || /parameters\[/.test(code);
  };

  // Extract parameter names from code
  const extractParametersFromCode = (code: string): Record<string, any> => {
    const detectedParams: Record<string, any> = {};
    
    if (!code || typeof code !== 'string') {
      return detectedParams;
    }

    // Pattern 1: parameters.paramName or parameters['paramName'] or parameters["paramName"]
    const dotNotationPattern = /parameters\.([a-zA-Z_$][a-zA-Z0-9_$]*)/g;
    const bracketNotationPattern = /parameters\[['"]([^'"]+)['"]\]/g;
    
    // Extract from dot notation: parameters.cel, parameters.apiKey, etc.
    let match;
    while ((match = dotNotationPattern.exec(code)) !== null) {
      const paramName = match[1];
      if (paramName && !detectedParams.hasOwnProperty(paramName)) {
        // Try to detect type from usage context
        const defaultValue = detectParameterDefaultValue(code, paramName);
        detectedParams[paramName] = defaultValue;
      }
    }
    
    // Extract from bracket notation: parameters['CNROCEL'], parameters["CMESSAG"], etc.
    while ((match = bracketNotationPattern.exec(code)) !== null) {
      const paramName = match[1];
      if (paramName && !detectedParams.hasOwnProperty(paramName)) {
        const defaultValue = detectParameterDefaultValue(code, paramName);
        detectedParams[paramName] = defaultValue;
      }
    }
    
    return detectedParams;
  };

  // Detect default value or type hint for a parameter
  const detectParameterDefaultValue = (code: string, paramName: string): any => {
    // Look for patterns like: const param = parameters.paramName || 'default'
    const defaultPattern = new RegExp(`(?:const|let|var)\\s+\\w+\\s*=\\s*parameters\\.${paramName}\\s*\\|\\|\\s*([^;\\n]+)`, 'i');
    const match = code.match(defaultPattern);
    
    if (match && match[1]) {
      const defaultValue = match[1].trim();
      // Try to parse as JSON value
      try {
        return JSON.parse(defaultValue);
      } catch {
        // If not JSON, return as string (remove quotes if present)
        return defaultValue.replace(/^['"]|['"]$/g, '');
      }
    }
    
    // Look for type hints in variable names or comments
    const lowerParam = paramName.toLowerCase();
    if (lowerParam.includes('id') || lowerParam.includes('num') || lowerParam.includes('cel')) {
      return '';
    } else if (lowerParam.includes('key') || lowerParam.includes('token') || lowerParam.includes('secret')) {
      return '';
    } else if (lowerParam.includes('message') || lowerParam.includes('msg') || lowerParam.includes('text')) {
      return '';
    } else if (lowerParam.includes('url') || lowerParam.includes('endpoint')) {
      return '';
    } else if (lowerParam.includes('count') || lowerParam.includes('limit') || lowerParam.includes('page')) {
      return 0;
    } else if (lowerParam.includes('enabled') || lowerParam.includes('active') || lowerParam.includes('flag')) {
      return false;
    }
    
    // Default to empty string
    return '';
  };

  // Handle code testing (Frontend execution)
  const handleTestCode = async () => {
    if (!generatedCode) {
      message.warning('No code to test');
      return;
    }

    // Check if code uses parameters and show modal if needed
    if (codeUsesParameters(generatedCode)) {
      // Extract parameters from code and pre-fill the modal
      const detectedParams = extractParametersFromCode(generatedCode);
      if (Object.keys(detectedParams).length > 0) {
        setTestParameters(JSON.stringify(detectedParams, null, 2));
      } else {
        setTestParameters('{}');
      }
      setTestParametersModalVisible(true);
      return;
    }

    // If no parameters needed, execute directly
    await executeTestCode({}, {});
  };

  // Execute test code with parameters
  const executeTestCode = async (parameters: any, context: any) => {
    setIsTesting(true);
    const startTime = Date.now();
    
    // Clear previous logs
    setHttpCallLogs([]);
    setExecutionLogs([]);
    setConsoleVisible(true);
    
    const logs: any[] = [];
    
    const addLog = (type: 'info' | 'error' | 'success' | 'warning', message: string, details?: any) => {
      const log = {
        id: Math.random().toString(36).substr(2, 9),
        timestamp: new Date().toISOString(),
        type,
        message,
        details
      };
      logs.push(log);
      setExecutionLogs(prev => [...prev, log]);
    };
    
    try {
      logger.info('🚀 Executing integration code in browser...');
      
      addLog('info', '🚀 Starting integration code execution...');
      
      // Import our custom BrowserHttpRequest
      const { BrowserHttpRequest } = await import('./BrowserHttpRequest');
      
      const httpCalls: any[] = [];
      
      addLog('info', '🔧 Creating monitored HttpRequest class...');
      
      // Create monitored HttpRequest that logs all calls
      class MonitoredHttpRequest {
        private baseHttpRequest: any;
        
        constructor(private host: string, private responseType?: string) {
          this.baseHttpRequest = new BrowserHttpRequest(host, responseType);
          addLog('info', `🌐 HttpRequest instance created for: ${host}`);
        }
        
        async get(path: string = '', params: object = {}, headers: any = {}) {
          const callStart = Date.now();
          const fullUrl = path ? `${this.host}/${path}` : this.host;
          
          addLog('info', `📤 Making GET request to ${fullUrl}`);
          
          try {
            const result = await this.baseHttpRequest.get(path, params, headers);
            const callTime = Date.now() - callStart;
            
            const callLog = {
              id: Math.random().toString(36).substr(2, 9),
              timestamp: new Date().toISOString(),
              method: 'GET',
              url: fullUrl,
              status: result.status,
              responseTime: callTime,
              success: result.status < 400,
              requestHeaders: headers,
              requestBody: params,
              responseHeaders: result.headers,
              responseBody: result.data
            };
            
            httpCalls.push(callLog);
            setHttpCallLogs(prev => [...prev, callLog]);
            
            addLog('success', `✅ GET request completed (${result.status}) in ${callTime}ms`);
            
            return result;
          } catch (error: any) {
            const callTime = Date.now() - callStart;
            
            const callLog = {
              id: Math.random().toString(36).substr(2, 9),
              timestamp: new Date().toISOString(),
              method: 'GET',
              url: fullUrl,
              status: 0,
              responseTime: callTime,
              success: false,
              requestHeaders: headers,
              requestBody: params,
              error: error.message
            };
            
            httpCalls.push(callLog);
            setHttpCallLogs(prev => [...prev, callLog]);
            
            addLog('error', `❌ GET request failed: ${error.message}`);
            
            throw error;
          }
        }
        
        async post(data: any = {}, path: string = '', headers: any = {}, timeout: number = 0) {
          const callStart = Date.now();
          const fullUrl = path ? `${this.host}/${path}` : this.host;
          
          addLog('info', `📤 Making POST request to ${fullUrl}`);
          
          try {
            const result = await this.baseHttpRequest.post(data, path, headers, timeout);
            const callTime = Date.now() - callStart;
            
            const callLog = {
              id: Math.random().toString(36).substr(2, 9),
              timestamp: new Date().toISOString(),
              method: 'POST',
              url: fullUrl,
              status: result.status,
              responseTime: callTime,
              success: result.status < 400,
              requestHeaders: headers,
              requestBody: data,
              responseHeaders: result.headers,
              responseBody: result.data
            };
            
            httpCalls.push(callLog);
            setHttpCallLogs(prev => [...prev, callLog]);
            
            addLog('success', `✅ POST request completed (${result.status}) in ${callTime}ms`);
            
            return result;
          } catch (error: any) {
            const callTime = Date.now() - callStart;
            
            const callLog = {
              id: Math.random().toString(36).substr(2, 9),
              timestamp: new Date().toISOString(),
              method: 'POST',
              url: fullUrl,
              status: 0,
              responseTime: callTime,
              success: false,
              requestHeaders: headers,
              requestBody: data,
              error: error.message
            };
            
            httpCalls.push(callLog);
            setHttpCallLogs(prev => [...prev, callLog]);
            
            addLog('error', `❌ POST request failed: ${error.message}`);
            
            throw error;
          }
        }
      }

      // Create simple Utils object for testing
      const BrowserUtils = {
        objected: (obj: any) => obj || {},
        isObject: (obj: any) => typeof obj === 'object' && obj !== null,
        isArray: (obj: any) => Array.isArray(obj),
        isString: (obj: any) => typeof obj === 'string'
      };

      // Store original console methods to avoid recursion
      const originalConsole = {
        log: window.console.log.bind(window.console),
        error: window.console.error.bind(window.console),
        warn: window.console.warn.bind(window.console)
      };

      // Create safe execution context
      const executionContext = {
        HttpRequest: MonitoredHttpRequest,
        Utils: BrowserUtils,
        parameters: parameters || {},
        context: context || {},
        console: {
          log: (...args: any[]) => originalConsole.log('🔧 [TEST]', ...args),
          error: (...args: any[]) => originalConsole.error('❌ [TEST]', ...args),
          warn: (...args: any[]) => originalConsole.warn('⚠️ [TEST]', ...args)
        },
        require: (module: string) => {
          addLog('info', `📦 Requiring module: ${module}`);
          if (module === './HttpRequest.class' || module.includes('HttpRequest')) {
            return { HttpRequest: MonitoredHttpRequest };
          }
          if (module === './Utils.class' || module.includes('Utils')) {
            return { Utils: BrowserUtils };
          }
          throw new Error(`Module ${module} not found in test environment`);
        },
        setTimeout,
        clearTimeout,
        fetch: window.fetch.bind(window),
        window: {
          HttpRequest: MonitoredHttpRequest,
          Utils: BrowserUtils,
          parameters: parameters || {},
          context: context || {}
        },
        Promise,
        JSON,
        Object,
        Array,
        String,
        Number,
        Date,
        Math,
        parseInt,
        parseFloat,
        encodeURIComponent,
        decodeURIComponent
      };

      addLog('info', '🚀 Preparing code execution environment...');

      // Execute the code with timeout
      const timeout = 10000; // 10 seconds
      const result = await Promise.race([
        new Promise(async (resolve, reject) => {
          try {
            // Set up execution context in window temporarily
            const originalValues: any = {};
            const excludedKeys = ['window', 'Promise', 'JSON', 'Object', 'Array', 'String', 'Number', 'Date', 'Math', 'setTimeout', 'clearTimeout', 'fetch', 'parseInt', 'parseFloat', 'encodeURIComponent', 'decodeURIComponent'];
            
            Object.keys(executionContext).forEach(key => {
              if (!excludedKeys.includes(key)) {
                originalValues[key] = (window as any)[key];
                (window as any)[key] = (executionContext as any)[key];
              }
            });

            // Also set parameters and context directly on window for easier access
            (window as any).parameters = parameters || {};
            (window as any).context = context || {};

            // Pre-process the generated code - inject HttpRequest automatically
            let processedCode = generatedCode;
            
            // Remove any existing HttpRequest imports
            processedCode = processedCode.replace(
              /const\s*{\s*HttpRequest\s*}\s*=\s*require\([^)]+\);?\s*/g,
              ''
            );
            
            // Automatically inject HttpRequest at the beginning
            processedCode = `const HttpRequest = window.HttpRequest;\n\n${processedCode}`;

            addLog('info', '📝 Executing generated code...');

            try {
              // Execute the processed code directly - it already has the IIFE pattern that executes immediately
              const executionResult = eval(`
                (function() {
                  ${processedCode}
                  return 'Code executed successfully!';
                })()
              `);
              
              addLog('info', '✅ Code executed successfully');
              addLog('info', `Execution result: ${executionResult}`);
              
              // Restore original window values
              Object.keys(originalValues).forEach(key => {
                if (originalValues[key] !== undefined) {
                  (window as any)[key] = originalValues[key];
                } else {
                  delete (window as any)[key];
                }
              });
              
              // Clean up parameters and context
              delete (window as any).parameters;
              delete (window as any).context;
              
              resolve({ message: 'Code executed successfully!' });
            } catch (execError) {
              // Restore original window values on error
              Object.keys(originalValues).forEach(key => {
                if (originalValues[key] !== undefined) {
                  (window as any)[key] = originalValues[key];
                } else {
                  delete (window as any)[key];
                }
              });
              
              // Clean up parameters and context
              delete (window as any).parameters;
              delete (window as any).context;
              
              addLog('error', `Code execution error: ${execError.message}`);
              reject(new Error(`Code execution error: ${execError.message}`));
            }
          } catch (error) {
            reject(error);
          }
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Test timeout after 10 seconds')), timeout)
        )
      ]);

      const executionTime = Date.now() - startTime;
      setTotalExecutionTime(executionTime);

      logger.info('✅ Test completed successfully:', result);
      logger.info('📊 HTTP calls made:', httpCalls);

      addLog('success', `✅ Code execution completed successfully in ${executionTime}ms`);
      
      if (result && typeof result === 'object') {
        addLog('info', `📄 Execution result: ${JSON.stringify(result).substring(0, 200)}${JSON.stringify(result).length > 200 ? '...' : ''}`);
      }

      message.success({
        content: `✅ Test passed! Execution time: ${executionTime}ms`,
        duration: 5,
      });
      
      if (httpCalls.length > 0) {
        const successfulCalls = httpCalls.filter(call => call.success).length;
        const totalCalls = httpCalls.length;
        addLog('info', `📡 Made ${successfulCalls}/${totalCalls} successful HTTP calls`);
        message.info({
          content: `📡 Made ${successfulCalls}/${totalCalls} successful HTTP calls`,
          duration: 4
        });
      }

    } catch (error: any) {
      const executionTime = Date.now() - startTime;
      setTotalExecutionTime(executionTime);
      logger.error('❌ Error executing integration:', error);
      
      let errorMessage = 'Unknown error occurred';
      
      if (error.message.includes('timeout')) {
        errorMessage = 'Code execution timed out (10s limit)';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'CORS error - API doesn\'t allow browser requests';
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error - Check your internet connection';
      } else if (error.message.includes('SyntaxError')) {
        errorMessage = 'Code syntax error - Check generated code';
      } else {
        errorMessage = error.message;
      }
      
      addLog('error', `❌ Execution failed: ${errorMessage}`);
      
      message.error({
        content: `❌ Test failed: ${errorMessage} (${executionTime}ms)`,
        duration: 8,
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Handle save (placeholder for now)
  // Clear console logs
  const handleClearConsole = () => {
    setHttpCallLogs([]);
    setExecutionLogs([]);
    setTotalExecutionTime(0);
  };

  const handleSaveCode = async () => {
    if (!generatedCode) {
      message.warning('No code to save');
      return;
    }

    if (!chatbotId) {
      message.error('Chatbot ID is required');
      return;
    }

    try {
      const response = await myAssistantServices.saveCustomIntegration({
        integrationName: aiPrompt || 'Custom Integration',
        generatedCode: generatedCode,
        chatbotId: chatbotId,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'Custom Integration Builder'
        }
      });

      const customMessage = {
        type: 'custom_integration',
        prompt: aiPrompt,
        generatedCode: generatedCode,
        timestamp: new Date().toISOString(),
        chatbotId: chatbotId,
        integrationId: response.id
      };

      const messageString = `@custom/integration ${JSON.stringify(customMessage)}`;
      // Pass only the message string, not an object
      onSave(messageString);
      message.success('Custom integration saved successfully');
    } catch (error) {
      logger.error('Error saving custom integration:', error);
      message.error('Failed to save custom integration');
    }
  };

  // Knowledge sources configuration
  const knowledgeSources: KnowledgeSource[] = [
    {
      title: 'Files',
      description: uploadedDocument 
        ? `Document "${uploadedDocument.name}" uploaded successfully.`
        : "Upload TXT, PDF, DOC, or DOCX files to expand your Custom Integration's knowledge base.",
      icon: <FileTextOutlined style={{ fontSize: '16px', color: 'white' }} />,
      action: uploadedDocument ? 'Change' : 'Upload',
      onClick: () => setFileModalVisible(true),
      iconBackground: uploadedDocument ? '#52c41a' : token.colorPrimary,
    },
    {
      title: 'Websites',
      description: syncedWebsite 
        ? `Website "${syncedWebsite}" synced successfully.`
        : 'Sync public websites to provide your Custom Integration with up-to-date information.',
      icon: <GlobalOutlined style={{ fontSize: '16px', color: 'white' }} />,
      action: syncedWebsite ? 'Change' : 'Sync',
      onClick: () => setWebsiteModalVisible(true),
      iconBackground: syncedWebsite ? '#52c41a' : token.colorPrimary,
    },
  ];

  // Render knowledge sources (replicating KnowledgeSources component style)
  const renderKnowledgeSources = () => (
    <Flex vertical gap={12} style={{ marginBottom: 16 }}>
      {knowledgeSources.map((source, index) => (
        <Flex
          key={index}
          align="center"
          justify="space-between"
          style={{
            padding: 16,
            background: '#fff',
            borderRadius: 8,
            width: '100%',
            border: '1px solid #f0f0f0',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
          }}
        >
          <Flex align="center" gap={16}>
            <div
              style={{
                background: source.iconBackground,
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {source.icon}
            </div>
            <Flex vertical>
              <Title level={5} style={{ margin: 0 }}>
                {source.title}
              </Title>
              <Text type="secondary" style={{ margin: 0 }}>
                {source.description}
              </Text>
            </Flex>
          </Flex>
          <Button onClick={source.onClick} style={{ width: '80px' }}>
            {source.action}
          </Button>
        </Flex>
      ))}
    </Flex>
  );

    return (
    <Layout>
      <Content style={{ padding: '16px', background: '#fff', height: '100%', overflow: 'hidden' }}>
        <Flex vertical gap={16} style={{ height: '100%', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
          {/* Header */}
          <Button
            type="link"
            icon={<LeftOutlined />}
            style={{ alignSelf: 'flex-start', paddingLeft: 0, color: '#00000095' }}
            onClick={showCodeEditor ? () => setShowCodeEditor(false) : onBack}
          >
            {showCodeEditor ? 'Back to Custom Integration' : 'Custom Integration'}
          </Button>

          {/* Show Code Editor View */}
          {showCodeEditor && generatedCode ? (
            <Card
              title="Generated Integration Code"
              style={{ 
                width: '100%',
                background: '#fff',
                border: '1px solid #d9d9d9'
              }}
              extra={
                <Space>
                  <Button 
                    icon={<BookOutlined />}
                    onClick={() => setDocsModalVisible(true)}
                    title="View HttpRequest documentation"
                  >
                    Docs
                  </Button>
                  <Button 
                    icon={<PlayCircleOutlined />}
                    onClick={handleTestCode}
                    loading={isTesting}
                    title="Execute the generated code directly in your browser (Frontend execution)"
                  >
                    {isTesting ? 'Testing...' : 'Test in Browser'}
                  </Button>
                  <Button 
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSaveCode}
                  >
                    Save
                  </Button>
                </Space>
              }
            >
              <div style={{ height: '350px', border: '1px solid #d9d9d9', borderRadius: '6px' }}>
                <Editor
                  height="350px"
                  defaultLanguage="typescript"
                  value={generatedCode}
                  theme="vs-dark"
                  options={{
                    readOnly: false,
                    minimap: { enabled: false },
                    fontSize: 14,
                    wordWrap: 'on',
                    automaticLayout: true,
                    scrollBeyondLastLine: false,
                    renderWhitespace: 'selection',
                    overviewRulerBorder: false,
                    hideCursorInOverviewRuler: true
                  }}
                  onChange={(value) => setGeneratedCode(value || '')}
                />
              </div>
            </Card>
          ) : null}

          {/* Console Viewer - Show when code editor is visible */}
          {showCodeEditor && (
            <ConsoleViewer
              httpCalls={httpCallLogs}
              executionLogs={executionLogs}
              totalExecutionTime={totalExecutionTime}
              visible={consoleVisible}
              onClear={handleClearConsole}
            />
          )}

          {/* Show main form only when not in code editor mode */}
          {!showCodeEditor && (
            <>
              {/* Knowledge Sources */}
              {renderKnowledgeSources()}

              {/* Generate with AI Card */}
              <Card
                title="Describe An Ideal Custom Integration"
                style={{ width: '100%' }}
                bodyStyle={{ padding: 16 }}
              >
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Describe the custom integration you want to create (optional). You need at least 2 of the following: description, uploaded file, or synced website.
                </Text>
                {integrationGeneratorConfig && integrationChatbotId ? (
                  <Chatbot
                    wsUrl={integrationGeneratorConfig.wsUrl}
                    chatbotId={integrationChatbotId}
                    channelId={channelId}
                    urlApi={`${process.env.REACT_APP_API_HOST}/chatbot`}
                    isWeb={true}
                    isPreview={false}
                    primaryColor={integrationGeneratorConfig.colorTitle}
                    headerColor={integrationGeneratorConfig.colorMain}
                    iconColor={integrationGeneratorConfig.iconBgColor}
                    botName={integrationGeneratorConfig.botName}
                    onArtifactAccept={handleArtifactAccept}
                    autoAcceptArtifacts={false}
                  />
                ) : (
                  <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
                    <Spin size="large" tip="Loading Custom Integration Generator..." />
                  </Flex>
                )}
              </Card>
            </>
          )}

          {/* File Upload Modal */}
          <Modal
            title="Upload File"
            open={fileModalVisible}
            onCancel={() => setFileModalVisible(false)}
            width="70%"
            style={{ maxWidth: 1000 }}
            footer={
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                <Button
                  key="submit"
                  type="primary"
                  loading={isFileUploading}
                  onClick={handleFileUpload}
                >
                  Upload
                </Button>
                <Button
                  key="cancel"
                  onClick={() => setFileModalVisible(false)}
                  style={{ marginLeft: 8 }}
                  disabled={isFileUploading}
                >
                  Cancel
                </Button>
              </div>
            }
          >
            <Dragger {...fileUploadProps} maxCount={1}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
              <p className="ant-upload-hint">
                Support for TXT, PDF, DOC, and DOCX files (max 5MB).
              </p>
            </Dragger>
          </Modal>

          {/* Website Sync Modal */}
          <Modal
            title="Add Website"
            open={websiteModalVisible}
            onCancel={() => setWebsiteModalVisible(false)}
            width="70%"
            style={{ maxWidth: 1000 }}
            footer={
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                <Button
                  key="submit"
                  type="primary"
                  loading={isWebsiteUploading}
                  onClick={handleWebsiteSync}
                >
                  Sync
                </Button>
                <Button
                  key="cancel"
                  onClick={() => setWebsiteModalVisible(false)}
                  style={{ marginLeft: 8 }}
                  disabled={isWebsiteUploading}
                >
                  Cancel
                </Button>
              </div>
            }
          >
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: token.marginSM }}>
                Enter the URL of the content you wish to synchronize.
              </Text>
              <Input
                placeholder="https://www.example.com"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                disabled={isWebsiteUploading}
              />
            </div>
          </Modal>

          {/* Test Parameters Modal */}
          <Modal
            title="Test Parameters"
            open={testParametersModalVisible}
            onCancel={() => setTestParametersModalVisible(false)}
            width="80%"
            style={{ maxWidth: 900 }}
            footer={
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button onClick={() => setTestParametersModalVisible(false)}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={async () => {
                    try {
                      // Parse parameters JSON
                      let parsedParameters = {};
                      let parsedContext = {};
                      
                      try {
                        parsedParameters = JSON.parse(testParameters || '{}');
                      } catch (e) {
                        message.error('Invalid JSON format for Parameters');
                        return;
                      }
                      
                      try {
                        parsedContext = JSON.parse(testContext || '{}');
                      } catch (e) {
                        message.error('Invalid JSON format for Context');
                        return;
                      }
                      
                      setTestParametersModalVisible(false);
                      await executeTestCode(parsedParameters, parsedContext);
                    } catch (error) {
                      message.error('Failed to parse parameters');
                    }
                  }}
                >
                  Run Test
                </Button>
              </div>
            }
          >
            <Flex vertical gap={16}>
              <div>
                <Title level={5}>Parameters</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Enter parameters as JSON object. These will be available as <code>parameters</code> in your code.
                </Text>
                <Input.TextArea
                  rows={6}
                  value={testParameters}
                  onChange={(e) => setTestParameters(e.target.value)}
                  placeholder='{"apiKey": "your-key", "userId": "123", "CNROCEL": "1234567890", "CMESSAG": "Test message"}'
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
              <div>
                <Title level={5}>Context (Optional)</Title>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Enter context as JSON object. This will be available as <code>context</code> in your code.
                </Text>
                <Input.TextArea
                  rows={4}
                  value={testContext}
                  onChange={(e) => setTestContext(e.target.value)}
                  placeholder='{"chatbotId": "xxx", "userId": "yyy"}'
                  style={{ fontFamily: 'monospace' }}
                />
              </div>
            </Flex>
          </Modal>

          {/* HttpRequest Documentation Modal */}
          <Modal
            title={
              <Space>
                <BookOutlined style={{ color: token.colorPrimary }} />
                <span>HttpRequest Class Documentation</span>
              </Space>
            }
            open={docsModalVisible}
            onCancel={() => setDocsModalVisible(false)}
            width="90%"
            style={{ maxWidth: 1200 }}
            footer={
              <Button onClick={() => setDocsModalVisible(false)}>
                Close
              </Button>
            }
          >
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                Complete guide on how to use the HttpRequest class in your custom integrations.
              </Text>

              <Collapse defaultActiveKey={['1']} ghost>
                <Panel header={<Title level={4} style={{ margin: 0 }}>🚀 Getting Started</Title>} key="1">
                  <div style={{ paddingLeft: 16 }}>
                    <Title level={5}>Creating an HttpRequest Instance</Title>
                    <div style={{ height: '120px', border: '1px solid #d9d9d9', borderRadius: 6, marginBottom: 16 }}>
                      <Editor
                        height="120px"
                        defaultLanguage="typescript"
                        value={`// Basic usage
const httpClient = new HttpRequest('https://api.example.com');

// With response type (optional)
const httpClient = new HttpRequest('https://api.example.com', 'json');`}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          lineNumbers: 'off',
                          folding: false,
                          renderLineHighlight: 'none',
                          overviewRulerBorder: false,
                          hideCursorInOverviewRuler: true,
                          contextmenu: false,
                          scrollbar: { vertical: 'hidden', horizontal: 'hidden' }
                        }}
                      />
                    </div>
                    <Text type="secondary">
                      The HttpRequest class is a wrapper around Axios that simplifies HTTP operations. 
                      Initialize it with your API base URL and optionally specify a response type.
                    </Text>
                  </div>
                </Panel>

                <Panel header={<Title level={4} style={{ margin: 0 }}>📥 GET Requests</Title>} key="2">
                  <div style={{ paddingLeft: 16 }}>
                    <Title level={5}>Basic GET Request</Title>
                    <div style={{ height: '200px', border: '1px solid #d9d9d9', borderRadius: 6, marginBottom: 16 }}>
                      <Editor
                        height="200px"
                        defaultLanguage="typescript"
                        value={`// Simple GET request
const response = await httpClient.get();

// GET with path
const response = await httpClient.get('users');

// GET with query parameters
const response = await httpClient.get('users', { page: 1, limit: 10 });

// GET with custom headers
const response = await httpClient.get('users', {}, {
  'Authorization': 'Bearer token123',
  'Custom-Header': 'value'
});`}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          lineNumbers: 'off',
                          folding: false,
                          renderLineHighlight: 'none',
                          overviewRulerBorder: false,
                          hideCursorInOverviewRuler: true,
                          contextmenu: false,
                          scrollbar: { vertical: 'auto', horizontal: 'hidden' }
                        }}
                      />
                    </div>

                    <Title level={5}>GET with Body (Special Case)</Title>
                    <div style={{ height: '100px', border: '1px solid #d9d9d9', borderRadius: 6, marginBottom: 16 }}>
                      <Editor
                        height="100px"
                        defaultLanguage="typescript"
                        value={`// GET request with data in body instead of query params
const response = await httpClient.getWithBody('search', {
  query: 'complex search data',
  filters: { category: 'tech', date: '2024' }
});`}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          lineNumbers: 'off',
                          folding: false,
                          renderLineHighlight: 'none',
                          overviewRulerBorder: false,
                          hideCursorInOverviewRuler: true,
                          contextmenu: false,
                          scrollbar: { vertical: 'hidden', horizontal: 'hidden' }
                        }}
                      />
                    </div>

                    <div style={{ background: '#e6f7ff', padding: 12, borderRadius: 6, border: '1px solid #91d5ff' }}>
                      <Text><strong>💡 Tip:</strong> Query parameters are automatically encoded in the URL for regular GET requests.</Text>
                    </div>
                  </div>
                </Panel>

                <Panel header={<Title level={4} style={{ margin: 0 }}>📤 POST Requests</Title>} key="3">
                  <div style={{ paddingLeft: 16 }}>
                    <Title level={5}>Creating Data</Title>
                    <div style={{ height: '280px', border: '1px solid #d9d9d9', borderRadius: 6, marginBottom: 16 }}>
                      <Editor
                        height="280px"
                        defaultLanguage="typescript"
                        value={`// Basic POST request
const newUser = {
  name: 'John Doe',
  email: 'john@example.com'
};
const response = await httpClient.post(newUser, 'users');

// POST with custom headers
const response = await httpClient.post(newUser, 'users', {
  'Authorization': 'Bearer token123',
  'Content-Type': 'application/json'
});

// POST with timeout (5 seconds)
const response = await httpClient.post(newUser, 'users', {}, 5000);`}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          lineNumbers: 'off',
                          folding: false,
                          renderLineHighlight: 'none',
                          overviewRulerBorder: false,
                          hideCursorInOverviewRuler: true,
                          contextmenu: false,
                          scrollbar: { vertical: 'auto', horizontal: 'hidden' }
                        }}
                      />
                    </div>

                    <div style={{ background: '#fff2e8', padding: 12, borderRadius: 6, border: '1px solid #ffbb96' }}>
                      <Text><strong>⚠️ Parameter Order:</strong> POST method uses this order: <code>post(data, path, headers, timeout)</code></Text>
                    </div>
                  </div>
                </Panel>

                <Panel header={<Title level={4} style={{ margin: 0 }}>🔄 PUT & PATCH Requests</Title>} key="4">
                  <div style={{ paddingLeft: 16 }}>
                    <Title level={5}>Updating Data</Title>
                    <div style={{ height: '300px', border: '1px solid #d9d9d9', borderRadius: 6, marginBottom: 16 }}>
                      <Editor
                        height="300px"
                        defaultLanguage="typescript"
                        value={`// PUT - Complete resource update
const updatedUser = {
  id: 123,
  name: 'John Smith',
  email: 'johnsmith@example.com'
};
const response = await httpClient.put('users/123', updatedUser);

// PATCH - Partial resource update
const partialUpdate = { name: 'John Smith Jr.' };
const response = await httpClient.patch('users/123', partialUpdate);

// With custom headers
const response = await httpClient.put('users/123', updatedUser, {
  'Authorization': 'Bearer token123'
});`}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          lineNumbers: 'off',
                          folding: false,
                          renderLineHighlight: 'none',
                          overviewRulerBorder: false,
                          hideCursorInOverviewRuler: true,
                          contextmenu: false,
                          scrollbar: { vertical: 'auto', horizontal: 'hidden' }
                        }}
                      />
                    </div>

                    <div style={{ background: '#f6ffed', padding: 12, borderRadius: 6, border: '1px solid #b7eb8f' }}>
                      <Text><strong>✅ Best Practice:</strong> Use PUT for complete updates and PATCH for partial updates.</Text>
                    </div>
                  </div>
                </Panel>

                <Panel header={<Title level={4} style={{ margin: 0 }}>🗑️ DELETE Requests</Title>} key="5">
                  <div style={{ paddingLeft: 16 }}>
                    <Title level={5}>Removing Data</Title>
                    <div style={{ height: '200px', border: '1px solid #d9d9d9', borderRadius: 6, marginBottom: 16 }}>
                      <Editor
                        height="200px"
                        defaultLanguage="typescript"
                        value={`// Basic DELETE request
const response = await httpClient.delete('users/123');

// DELETE with additional data (if needed)
const response = await httpClient.delete('users/123', {
  reason: 'User requested deletion'
});

// DELETE with authorization
const response = await httpClient.delete('users/123', {}, {
  'Authorization': 'Bearer token123'
});`}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          lineNumbers: 'off',
                          folding: false,
                          renderLineHighlight: 'none',
                          overviewRulerBorder: false,
                          hideCursorInOverviewRuler: true,
                          contextmenu: false,
                          scrollbar: { vertical: 'auto', horizontal: 'hidden' }
                        }}
                      />
                    </div>
                  </div>
                </Panel>

                <Panel header={<Title level={4} style={{ margin: 0 }}>🔍 Response Handling</Title>} key="6">
                  <div style={{ paddingLeft: 16 }}>
                    <Title level={5}>Working with Responses</Title>
                    <div style={{ height: '450px', border: '1px solid #d9d9d9', borderRadius: 6, marginBottom: 16 }}>
                      <Editor
                        height="450px"
                        defaultLanguage="typescript"
                        value={`// Standard response structure
const response = await httpClient.get('users');

// Accessing response data
logger.info('Status:', response.status);        // 200, 404, 500, etc.
logger.info('Data:', response.data);           // Response body
logger.info('Headers:', response.headers);     // Response headers

// Check if request was successful
if (response.status >= 200 && response.status < 300) {
  logger.info('Success:', response.data);
} else {
  logger.info('Error:', response.status);
}

// Working with different status codes
switch (response.status) {
  case 200:
    logger.info('Success:', response.data);
    break;
  case 404:
    logger.info('Resource not found');
    break;
  case 500:
    logger.info('Server error');
    break;
  default:
    logger.info('Unexpected status:', response.status);
}`}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          lineNumbers: 'off',
                          folding: false,
                          renderLineHighlight: 'none',
                          overviewRulerBorder: false,
                          hideCursorInOverviewRuler: true,
                          contextmenu: false,
                          scrollbar: { vertical: 'auto', horizontal: 'hidden' }
                        }}
                      />
                    </div>
                  </div>
                </Panel>

                <Panel header={<Title level={4} style={{ margin: 0 }}>⚙️ Advanced Configuration</Title>} key="7">
                  <div style={{ paddingLeft: 16 }}>
                    <Title level={5}>Headers and Authentication</Title>
                    <div style={{ height: '300px', border: '1px solid #d9d9d9', borderRadius: 6, marginBottom: 16 }}>
                      <Editor
                        height="300px"
                        defaultLanguage="typescript"
                        value={`// Common authentication patterns
const headers = {
  'Authorization': 'Bearer your-jwt-token',
  'Content-Type': 'application/json',
  'Accept': 'application/json'
};

// API Key authentication
const apiKeyHeaders = {
  'X-API-Key': 'your-api-key',
  'Authorization': 'API-Key your-api-key'
};

// Basic authentication
const basicAuth = {
  'Authorization': 'Basic ' + btoa('username:password')
};`}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          lineNumbers: 'off',
                          folding: false,
                          renderLineHighlight: 'none',
                          overviewRulerBorder: false,
                          hideCursorInOverviewRuler: true,
                          contextmenu: false,
                          scrollbar: { vertical: 'auto', horizontal: 'hidden' }
                        }}
                      />
                    </div>

                    <Title level={5}>Timeout Configuration</Title>
                    <div style={{ height: '80px', border: '1px solid #d9d9d9', borderRadius: 6, marginBottom: 16 }}>
                      <Editor
                        height="80px"
                        defaultLanguage="typescript"
                        value={`// Set timeout for POST requests (in milliseconds)
const response = await httpClient.post(data, 'users', {}, 10000); // 10 second timeout

// Timeout of 0 means no timeout (default)`}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          lineNumbers: 'off',
                          folding: false,
                          renderLineHighlight: 'none',
                          overviewRulerBorder: false,
                          hideCursorInOverviewRuler: true,
                          contextmenu: false,
                          scrollbar: { vertical: 'hidden', horizontal: 'hidden' }
                        }}
                      />
                    </div>
                  </div>
                </Panel>

                <Panel header={<Title level={4} style={{ margin: 0 }}>🛠️ Complete Example</Title>} key="8">
                  <div style={{ paddingLeft: 16 }}>
                    <Title level={5}>Real-world Integration Example</Title>
                    <div style={{ height: '600px', border: '1px solid #d9d9d9', borderRadius: 6, marginBottom: 16 }}>
                      <Editor
                        height="600px"
                        defaultLanguage="typescript"
                        value={`// Complete example: User management integration
(async function() {
  // Initialize HTTP client
  const api = new HttpRequest('https://jsonplaceholder.typicode.com');
  
  try {
    // 1. Get all users
    logger.info('🔍 Fetching users...');
    const usersResponse = await api.get('users');
    logger.info('Users found:', usersResponse.data.length);
    
    // 2. Get specific user
    const userResponse = await api.get('users/1');
    logger.info('User details:', userResponse.data.name);
    
    // 3. Create new user
    const newUser = {
      name: 'Custom Integration User',
      email: 'integration@example.com',
      username: 'integration_user'
    };
    
    logger.info('📝 Creating new user...');
    const createResponse = await api.post(newUser, 'users');
    logger.info('Created user ID:', createResponse.data.id);
    
    // 4. Update user
    const updatedUser = {
      ...newUser,
      name: 'Updated Integration User'
    };
    
    logger.info('🔄 Updating user...');
    const updateResponse = await api.put('users/1', updatedUser);
    logger.info('Updated user:', updateResponse.data.name);
    
    // 5. Delete user
    logger.info('🗑️ Deleting user...');
    const deleteResponse = await api.delete('users/1');
    logger.info('Delete status:', deleteResponse.status);
    
    logger.info('✅ Integration completed successfully!');
    
  } catch (error) {
    logger.error('❌ Integration failed:', error);
  }
})();`}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          lineNumbers: 'off',
                          folding: false,
                          renderLineHighlight: 'none',
                          overviewRulerBorder: false,
                          hideCursorInOverviewRuler: true,
                          contextmenu: false,
                          scrollbar: { vertical: 'auto', horizontal: 'hidden' }
                        }}
                      />
                    </div>
                  </div>
                </Panel>

                <Panel header={<Title level={4} style={{ margin: 0 }}>📋 Method Reference</Title>} key="9">
                  <div style={{ paddingLeft: 16 }}>
                    <div style={{ background: '#fafafa', padding: 16, borderRadius: 6 }}>
                      <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <div>
                          <Tag color="blue">GET</Tag>
                          <code>get(path?, params?, headers?)</code>
                          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                            Fetch data from the server. Parameters go in URL query string.
                          </Text>
                        </div>

                        <div>
                          <Tag color="blue">GET</Tag>
                          <code>getWithBody(path, params?, headers?)</code>
                          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                            GET request with data in the request body instead of URL parameters.
                          </Text>
                        </div>

                        <div>
                          <Tag color="green">POST</Tag>
                          <code>post(data?, path?, headers?, timeout?)</code>
                          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                            Create new resources. Data goes in the request body.
                          </Text>
                        </div>

                        <div>
                          <Tag color="orange">PUT</Tag>
                          <code>put(path, data?, headers?)</code>
                          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                            Update entire resources. Replaces all fields.
                          </Text>
                        </div>

                        <div>
                          <Tag color="orange">PATCH</Tag>
                          <code>patch(path, data?, headers?)</code>
                          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                            Partially update resources. Only updates specified fields.
                          </Text>
                        </div>

                        <div>
                          <Tag color="red">DELETE</Tag>
                          <code>delete(path, data?, headers?)</code>
                          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                            Remove resources from the server.
                          </Text>
                        </div>
                      </Space>
                    </div>

                    <Divider />

                    <Title level={5}>Response Object Structure</Title>
                    <div style={{ height: '120px', border: '1px solid #d9d9d9', borderRadius: 6 }}>
                      <Editor
                        height="120px"
                        defaultLanguage="typescript"
                        value={`{
  status: number,    // HTTP status code (200, 404, 500, etc.)
  data: any,         // Response body/payload
  headers: object,   // Response headers
  // ... other Axios response properties
}`}
                        theme="vs-dark"
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          fontSize: 13,
                          wordWrap: 'on',
                          automaticLayout: true,
                          scrollBeyondLastLine: false,
                          lineNumbers: 'off',
                          folding: false,
                          renderLineHighlight: 'none',
                          overviewRulerBorder: false,
                          hideCursorInOverviewRuler: true,
                          contextmenu: false,
                          scrollbar: { vertical: 'hidden', horizontal: 'hidden' }
                        }}
                      />
                    </div>
                  </div>
                </Panel>
              </Collapse>
            </div>
          </Modal>
        </Flex>
      </Content>
    </Layout>
  );
}; 